/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Grommet } from 'grommet'
import {
  AdvisoryActivityIcons,
  HeaderFour,
  TabButton,
  TabSet,
} from 'secondstep-components'

import { launchPresentation } from 'utils/playerHelper'
import HsIndividualResourceActivityDetails from 'components/HsIndividualResourceActivityDetails'
import HsIndividualResourceMetadata from 'components/HsIndividualResourceMetadata'
import HsIndividualResourceReferences from 'components/HsIndividualResourceReferences'
import HsIndividualResourceMarkComplete from 'components/HsIndividualResourceMarkComplete'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import HighSchoolPreviewBanner from 'components/HighSchoolPreviewBanner'
import TieredInterventionMarkComplete from 'components/TieredInterventionMarkComplete'
import BackLink from 'components/BackLink'
import { getAudienceMetadata, getGradeMetadata } from 'utils/highschoolHelpers'
import {
  extractAdvisoryResourcesFromActivityDetails,
  extractGradeValue,
  extractUnitValue,
  getAdvisoryIconColorByProgram,
  getAdvisoryActivityHref,
  getPhaseSections,
  groupAdvisoryActivitiesByType,
  isAdvisoryActivitiesHeaderBlock,
  getBreadcrumbPath,
  getBreadcrumbText,
  normalizePhaseSection,
  SECTION_PHASES,
} from './helpers'
import { getLanguageToggle } from 'components/HsIndividualResourceActivityDetails/helpers'
import { isResourceComplete } from 'utils/resourceCompletionHelpers'
import {
  getCurrentProgram,
  PROGRAM_TYPES,
  getProgramTheme,
} from 'utils/programHelpers'
import {
  tabTheme,
  ActivityButton,
  AudienceTagContainer,
  ControlPanel,
  DetailsContainer,
  ExternalLinkIcon,
  HeaderContainer,
  PageContainer,
  StyledHeaderOne,
  SurveyLink,
  BannerSpacer,
  SiteSelectorWrapper,
  HsIndividualResourceMarkCompleteWrapper,
  Divider,
  HeaderTitleContainer,
  TitleWrapper,
  HeaderContentWrapper,
  MarkCompleteContainer,
  MetadataContainer,
  PhaseContentWrapper,
  PhaseSectionBody,
  ResourceContainer,
  BreadcrumbWrapper,
  NavigationWrapper,
  SectionButtonRow,
  SectionContainer,
  SectionHeaderRow,
  SectionTitle,
  StyledMarkdownParagraph,
  ResourceImageContainer,
  PreviewBannerWrapper,
  AdvisoryActivitiesContainer,
  AdvisoryActivitiesTitle,
  AdvisoryActivitiesGrid,
  AdvisoryInlineLink,
  AdvisoryInlineLinkRow,
  AdvisoryInlineLinks,
  PhaseTextLinkWithFileIcon,
  WeeklyPlanContentContainer,
  WeeklyPlanHeaderContainer,
  WeeklyPlanOuterContainer,
} from './component.styles'
import {
  SURVEY_TEXT,
  LAUNCH_ACTIVITY_BUTTON_TEXT,
  LAUNCH_LESSON_BUTTON_TEXT,
  PREVIEW_BUTTON_TEXT,
} from './constants'
import externalLink from './assets/external_link.svg'
import SiteSelector from 'components/SiteSelector'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContextLegacy'
import AdvisoryActivityLink from 'experienceComponents/AdvisoryActivityLink'
import ImageFullWidth from 'experienceComponents/ImageFullWidth'
import HeaderBodyCopy from 'experienceComponents/HeaderBodyCopy'
import ImageBodyCopyFlexible from 'components/ImageBodyCopyFlexible'
import InlineVideo from 'components/InlineVideo'
import contentTypes from 'utils/contentfulParser/contents'
import { toCamelCase, toPascalCase } from 'utils/stringHelpers'

const VIDEO_VARIANT = 'highSchool'

const CONTENT_MAPPING = {
  headerBodyCopy: {
    component: HeaderBodyCopy,
    parser: (content, handlers) => ({
      ...contentTypes.headerBodyCopy(content),
      ...handlers,
    }),
  },
  h4: {
    component: HeaderFour,
    parser: content => ({
      children: content?.h4Content,
      dataTestId: content?.h4Content?.toLowerCase()?.replace(' ', '-'),
    }),
  },
  textLinkIcon: {
    component: PhaseTextLinkWithFileIcon,
    parser: contentTypes.textLinkIcon,
  },
  image: {
    component: ImageFullWidth,
    parser: content => ({
      imageThumbnailUrl: content?.image?.file?.url || content?.image?.url,
      imageUrl: content?.image?.file?.url || content?.image?.url,
      title: '',
      isZoomable: true,
    }),
  },
  imageBodyCopyFlexible: {
    component: ImageBodyCopyFlexible,
    parser: content => ({
      ...contentTypes.imageBodyCopyFlexible(content),
      isZoomable: true,
      title: '',
    }),
  },
  video: {
    component: InlineVideo,
    parser: content => ({
      entryId: content?.id,
      locale: content?.locale,
      variant: VIDEO_VARIANT,
    }),
  },
}

const renderContentComponent = ({ contentBlock, key, handlers }) => {
  const mapping =
    CONTENT_MAPPING[contentBlock?.contentType?.id] ||
    CONTENT_MAPPING[toCamelCase(contentBlock?.__typename)]

  if (!mapping) {
    return null
  }

  const ContentComponent = mapping.component
  return (
    <ContentComponent key={key} {...mapping.parser(contentBlock, handlers)} />
  )
}

const resolveAdvisoryIcon = advisoryActivity => {
  const iconNameCandidates = [
    advisoryActivity?.advisoryIcon,
    advisoryActivity?.advisoryActivityType,
  ].filter(Boolean)

  for (const iconNameCandidate of iconNameCandidates) {
    const camelCaseName = toCamelCase(iconNameCandidate)
    const singularCamelCaseName = camelCaseName.endsWith('s')
      ? camelCaseName.slice(0, -1)
      : camelCaseName

    const iconFromCandidate =
      AdvisoryActivityIcons[toPascalCase(camelCaseName)] ||
      AdvisoryActivityIcons[toPascalCase(singularCamelCaseName)]

    if (iconFromCandidate) {
      return iconFromCandidate
    }
  }

  return null
}

const IndividualResource = ({
  entry,
  gradeInstructorData,
  userContext,
  navigationEvent,
  isHsPreview,
  isPreview,
  productTabTitle,
  match,
  routeContent,
}) => {
  if (!entry) return null

  const {
    activityDetails,
    facetMetadataSetting,
    image,
    markComplete: markCompleteContent,
    presentation,
    references,
    resourceMetadata,
    surveyLink,
    languageToggle: entryLanguageToggle,
  } = entry || {}
  const { changeLocale, locale: routeLocale } = routeContent || {}
  const { metadataValues } = resourceMetadata || {}
  const [activeActivityIndex, setActiveActivityIndex] = useState(0)
  const [reference, setReference] = useState()
  const [openAccordionReference, setOpenAccordionReference] = useState(false)
  const supRef = useRef()

  const scrollToBottom = () => {
    setTimeout(() => supRef.current.scrollIntoView({ behavior: 'smooth' }), 500)
  }

  const { description, displayTitle, courseNodeConnector } =
    resourceMetadata || {}
  const { courseNodeEntryPoint: courseNodeId } = courseNodeConnector || {}
  const audience = getAudienceMetadata(resourceMetadata)
  const grades = getGradeMetadata(resourceMetadata)
  const currentProgram = getCurrentProgram()
  const isHighSchool = currentProgram === PROGRAM_TYPES.HIGH_SCHOOL
  const activityDetailsList = Array.isArray(activityDetails)
    ? activityDetails
    : []
  const activeActivityDetail =
    activityDetailsList[activeActivityIndex] || activityDetailsList[0] || null
  const resolvedPresentation =
    presentation ||
    activeActivityDetail?.presentation ||
    resourceMetadata?.presentation ||
    entry?.metadata?.presentation ||
    null
  const resolvedMarkCompleteContent =
    markCompleteContent ||
    activeActivityDetail?.markComplete ||
    resourceMetadata?.markComplete ||
    entry?.metadata?.markComplete ||
    null

  const handleReferences = refClicked => {
    const referenceClicked = refClicked.replace(/[^\w\s]/gi, '')
    if (refClicked && referenceClicked) {
      setOpenAccordionReference(true)
      setReference(referenceClicked)
      scrollToBottom()
    }
  }

  const handleActivityTabs = activityIndex => {
    if (openAccordionReference) {
      setOpenAccordionReference(!openAccordionReference)
    }
    setReference(null)
    setActiveActivityIndex(activityIndex)
  }

  useEffect(() => {
    if (displayTitle) {
      document.title = `${productTabTitle} | ${displayTitle}`
    }
  }, [displayTitle, productTabTitle])

  const launchActivity = (isPreviewLaunch = false) => {
    if (!resolvedPresentation?.id) return
    const { id } = resolvedPresentation
    const theme = getProgramTheme()
    const mode = isPreviewLaunch ? 'preview' : ''
    const lessonTitle = resourceMetadata?.routeSlug
    const extraQueryParams = isPreviewLaunch ? '&tier=2' : ''
    const siteId = userContext?.sites?.[0]?.siteId
    const activeSiteContext = (() => {
      const sites = Array.isArray(userContext?.sites) ? userContext.sites : []
      if (!sites.length) {
        return userContext
      }

      const matchedSite =
        sites.find(site => String(site?.siteId) === String(siteId)) || sites[0]

      return {
        ...userContext,
        sites: [matchedSite],
      }
    })()

    launchPresentation({
      entryId: id,
      locale: 'en-US',
      mode,
      navigationEvent,
      theme,
      lessonTitle,
      extraQueryParams,
      userContext: activeSiteContext,
      siteId,
    })
  }

  const shouldShowMarkCompleteHs = ({ markCompleteContent, isPreview }) => {
    if (isPreview) return false
    if (!markCompleteContent) return false
    if (currentProgram !== PROGRAM_TYPES.HIGH_SCHOOL) return false
    return true
  }

  const shouldShowTieredInterventionsMarkComplete = ({ isPreview }) => {
    if (isPreview) return false
    if (currentProgram === PROGRAM_TYPES.HIGH_SCHOOL) return false
    return true
  }

  const gradeValue = extractGradeValue(metadataValues)
  const unitValue = extractUnitValue(metadataValues)
  const audienceTypeTag = unitValue
  const audienceGradeTag = isHighSchool ? grades : gradeValue?.toUpperCase()
  const phaseSections = getPhaseSections({
    activityDetailsList,
    activeActivityDetail,
    entry,
  })
  const beforeSection = phaseSections[SECTION_PHASES.BEFORE]
  const duringSection = phaseSections[SECTION_PHASES.DURING]
  const afterSection = phaseSections[SECTION_PHASES.AFTER]
  const normalizedBeforeSection = normalizePhaseSection({
    section: beforeSection,
    fallbackTitle: 'Before',
  })
  const normalizedDuringSection = normalizePhaseSection({
    section: duringSection,
    fallbackTitle: 'During',
  })
  const normalizedAfterSection = normalizePhaseSection({
    section: afterSection,
    fallbackTitle: 'After',
  })
  const hasPhaseSections = !!(beforeSection || duringSection || afterSection)
  const advisoryResources =
    Array.isArray(entry?.additionalResources) &&
    entry.additionalResources.length
      ? entry.additionalResources
      : extractAdvisoryResourcesFromActivityDetails({
          activityDetailsList,
        })

  const advisoryActivityGroups = groupAdvisoryActivitiesByType(
    advisoryResources,
  )
  const flattenedAdvisoryActivities = Object.values(
    advisoryActivityGroups,
  ).flat()
  const showAdvisoryActivities = Object.keys(advisoryActivityGroups).length > 0
  const advisoryIconColor = getAdvisoryIconColorByProgram(currentProgram)
  const breadcrumbText = getBreadcrumbText(currentProgram, productTabTitle)

  return (
    <PageContainer className="individual-resource-print-page">
      {isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <HighSchoolPreviewBanner />
        </PreviewBannerWrapper>
      )}
      {isPreview && !isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <CoursePreviewBanner />
        </PreviewBannerWrapper>
      )}

      {!isPreview && (
        <NavigationWrapper data-print-hidden="navigation">
          {!isHighSchool && (
            <BreadcrumbWrapper>
              <BackLink
                dataTestId="breadcrumb"
                onClick={() =>
                  navigationEvent(getBreadcrumbPath(currentProgram))
                }
              >
                {breadcrumbText}
              </BackLink>
            </BreadcrumbWrapper>
          )}
          <SiteSelectorWrapper isHighSchool={isHighSchool}>
            <SiteSelector />
          </SiteSelectorWrapper>
        </NavigationWrapper>
      )}
      <HeaderContainer>
        <div className="content">
          <HeaderContentWrapper isHighSchool={isHighSchool}>
            <AudienceTagContainer
              audience={audience}
              isHighSchool={isHighSchool}
            >
              {!!audienceTypeTag && <div>{audienceTypeTag}</div>}
              {!!audienceGradeTag && <div>{audienceGradeTag}</div>}
            </AudienceTagContainer>
            <HeaderTitleContainer>
              <TitleWrapper>
                <StyledHeaderOne>{displayTitle}</StyledHeaderOne>
              </TitleWrapper>
              {!hasPhaseSections &&
                shouldShowTieredInterventionsMarkComplete({
                  markCompleteContent: resolvedMarkCompleteContent,
                  isPreview,
                }) && (
                  <div data-print-hidden="mark-done">
                    <TieredInterventionMarkComplete
                      allowAnyRegistrationMatch
                      courseNodeId={courseNodeId}
                      instructorData={gradeInstructorData}
                      isComplete={isResourceComplete({
                        allowAnyRegistrationMatch: true,
                        instructorData: gradeInstructorData,
                        courseNodeId,
                        program: currentProgram,
                      })}
                      match={match}
                      program={currentProgram}
                      strictCompletionUpdate
                      useProgramFlexRefresh
                      userContext={userContext}
                    />
                  </div>
                )}
            </HeaderTitleContainer>
            <StyledMarkdownParagraph small>
              {description}
            </StyledMarkdownParagraph>
            {!!surveyLink && (
              <SurveyLink
                href={surveyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {SURVEY_TEXT}
                <ExternalLinkIcon
                  alt={'External Link Icon'}
                  src={externalLink}
                />
              </SurveyLink>
            )}
            {!hasPhaseSections &&
              shouldShowMarkCompleteHs({
                markCompleteContent: resolvedMarkCompleteContent,
                isPreview,
              }) && (
                <HsIndividualResourceMarkCompleteWrapper data-print-hidden="mark-done">
                  <HsIndividualResourceMarkComplete
                    activityType={audience}
                    courseNodeId={courseNodeId}
                    isV3
                    markCompleteContent={resolvedMarkCompleteContent}
                    practiceName={displayTitle}
                  />
                </HsIndividualResourceMarkCompleteWrapper>
              )}
          </HeaderContentWrapper>
          {isHighSchool && !!image?.url && (
            <ResourceImageContainer
              alt={image?.description}
              className="resource-image"
              src={image?.url}
            />
          )}
        </div>
      </HeaderContainer>
      <ResourceContainer isHighSchool={isHighSchool}>
        <MetadataContainer isHighSchool={isHighSchool}>
          <HsIndividualResourceMetadata
            audience={audience}
            facetMetadataSetting={facetMetadataSetting}
            program={currentProgram}
            resourceMetadata={resourceMetadata}
          />
        </MetadataContainer>
        {isHighSchool && !hasPhaseSections && (
          <ControlPanel>
            <div className="content">
              <Grommet theme={tabTheme}>
                <TabSet>
                  {activityDetailsList.map((activity, index) => (
                    <TabButton
                      dataTestId={`${activity.displayTitle
                        .toLowerCase()
                        .replace(' ', '-')}-tab`}
                      isActive={index === activeActivityIndex}
                      key={`${activity.displayTitle.toLowerCase()}-${index}`}
                      label={activity.displayTitle}
                      onClick={() => handleActivityTabs(index)}
                    />
                  ))}
                </TabSet>
              </Grommet>
              {!!resolvedPresentation && (
                <ActivityButton
                  $isHighSchoolButton={isHighSchool}
                  onClick={() => launchActivity(isPreview)}
                >
                  {LAUNCH_ACTIVITY_BUTTON_TEXT}
                </ActivityButton>
              )}
            </div>
          </ControlPanel>
        )}
        <DetailsContainer audience={audience} isHighSchool={isHighSchool}>
          <div className="content">
            {hasPhaseSections ? (
              <WeeklyPlanOuterContainer
                audience={audience}
                isHighSchool={isHighSchool}
              >
                {!isHighSchool && (
                  <WeeklyPlanHeaderContainer>
                    Weekly Plan
                  </WeeklyPlanHeaderContainer>
                )}
                <WeeklyPlanContentContainer>
                  <PhaseContentWrapper>
                    {!!normalizedBeforeSection && (
                      <SectionContainer>
                        <SectionHeaderRow>
                          <SectionTitle>
                            {normalizedBeforeSection?.displayTitle || 'Before'}
                          </SectionTitle>
                          {!!resolvedPresentation && !isHighSchool && (
                            <SectionButtonRow data-print-hidden="preview-button-row">
                              <ActivityButton
                                onClick={() => launchActivity(true)}
                              >
                                {PREVIEW_BUTTON_TEXT}
                              </ActivityButton>
                            </SectionButtonRow>
                          )}
                        </SectionHeaderRow>
                        <PhaseSectionBody isHighSchool={isHighSchool}>
                          {(normalizedBeforeSection?.content || []).map(
                            (content, index) =>
                              renderContentComponent({
                                contentBlock: content,
                                key: `before-${index}`,
                                handlers: {
                                  referenceHandler: handleReferences,
                                  presentation: resolvedPresentation,
                                  launchActivity,
                                  isPreview,
                                },
                              }),
                          )}
                        </PhaseSectionBody>
                      </SectionContainer>
                    )}

                    {!!normalizedDuringSection && (
                      <>
                        <Divider />
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedDuringSection?.displayTitle ||
                                'During'}
                            </SectionTitle>
                            <SectionButtonRow data-print-hidden="during-controls-row">
                              {!!resolvedPresentation && (
                                <ActivityButton
                                  $isHighSchoolButton={isHighSchool}
                                  onClick={() => launchActivity(false)}
                                >
                                  {isHighSchool
                                    ? LAUNCH_ACTIVITY_BUTTON_TEXT
                                    : LAUNCH_LESSON_BUTTON_TEXT}
                                </ActivityButton>
                              )}
                              {!isHighSchool &&
                                shouldShowTieredInterventionsMarkComplete({
                                  markCompleteContent: resolvedMarkCompleteContent,
                                  isPreview,
                                }) && (
                                  <MarkCompleteContainer
                                    $isHighSchool={isHighSchool}
                                    data-print-hidden="mark-done"
                                  >
                                    <TieredInterventionMarkComplete
                                      allowAnyRegistrationMatch
                                      courseNodeId={courseNodeId}
                                      instructorData={gradeInstructorData}
                                      isComplete={isResourceComplete({
                                        allowAnyRegistrationMatch: true,
                                        instructorData: gradeInstructorData,
                                        courseNodeId,
                                        program: currentProgram,
                                      })}
                                      match={match}
                                      program={currentProgram}
                                      strictCompletionUpdate
                                      useProgramFlexRefresh
                                      userContext={userContext}
                                    />
                                  </MarkCompleteContainer>
                                )}
                              {isHighSchool &&
                                shouldShowMarkCompleteHs({
                                  markCompleteContent: resolvedMarkCompleteContent,
                                  isPreview,
                                }) && (
                                  <MarkCompleteContainer
                                    $isHighSchool={isHighSchool}
                                    data-print-hidden="mark-done"
                                  >
                                    <HsIndividualResourceMarkComplete
                                      activityType={audience}
                                      courseNodeId={courseNodeId}
                                      isInline
                                      isV3
                                      markCompleteContent={
                                        resolvedMarkCompleteContent
                                      }
                                      practiceName={displayTitle}
                                    />
                                  </MarkCompleteContainer>
                                )}
                            </SectionButtonRow>
                          </SectionHeaderRow>
                          <PhaseSectionBody isHighSchool={isHighSchool}>
                            {(normalizedDuringSection?.content || []).map(
                              (content, index) =>
                                renderContentComponent({
                                  contentBlock: content,
                                  key: `during-${index}`,
                                  handlers: {
                                    referenceHandler: handleReferences,
                                    presentation: resolvedPresentation,
                                    launchActivity,
                                    isPreview,
                                  },
                                }),
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}

                    {!!normalizedAfterSection && (
                      <>
                        <Divider />
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedAfterSection?.displayTitle || 'After'}
                            </SectionTitle>
                          </SectionHeaderRow>
                          <PhaseSectionBody isHighSchool={isHighSchool}>
                            {(normalizedAfterSection?.content || []).map(
                              (content, index) => (
                                <React.Fragment key={`after-fragment-${index}`}>
                                  {renderContentComponent({
                                    contentBlock: content,
                                    key: `after-${index}`,
                                    handlers: {
                                      referenceHandler: handleReferences,
                                      presentation: resolvedPresentation,
                                      launchActivity,
                                      isPreview,
                                    },
                                  })}
                                  {showAdvisoryActivities &&
                                    isAdvisoryActivitiesHeaderBlock(
                                      content,
                                    ) && (
                                      <AdvisoryInlineLinks>
                                        {flattenedAdvisoryActivities.map(
                                          activity => {
                                            const advisoryHref = getAdvisoryActivityHref(
                                              {
                                                productName:
                                                  match?.params?.program ||
                                                  'middle-school',
                                                activity,
                                              },
                                            )
                                            if (!advisoryHref) {
                                              return null
                                            }

                                            const AdvisoryIcon = resolveAdvisoryIcon(
                                              activity,
                                            )

                                            return (
                                              <AdvisoryInlineLinkRow
                                                iconColor={advisoryIconColor}
                                                key={activity.id}
                                              >
                                                {!!AdvisoryIcon && (
                                                  <span className="advisory-inline-icon">
                                                    <AdvisoryIcon />
                                                  </span>
                                                )}
                                                <AdvisoryInlineLink
                                                  href={advisoryHref}
                                                >
                                                  {activity.displayTitle}
                                                </AdvisoryInlineLink>
                                              </AdvisoryInlineLinkRow>
                                            )
                                          },
                                        )}
                                      </AdvisoryInlineLinks>
                                    )}
                                </React.Fragment>
                              ),
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}
                  </PhaseContentWrapper>
                </WeeklyPlanContentContainer>
              </WeeklyPlanOuterContainer>
            ) : (
              !!activityDetailsList[activeActivityIndex] && (
                <HsIndividualResourceActivityDetails
                  audience={audience}
                  details={activityDetailsList[activeActivityIndex]}
                  getEntryWithNewLocale={changeLocale}
                  isPreview={isPreview}
                  languageToggle={
                    getLanguageToggle(
                      activityDetailsList[activeActivityIndex],
                    ) ||
                    entryLanguageToggle ||
                    null
                  }
                  launchActivity={launchActivity}
                  launchButtonText={LAUNCH_LESSON_BUTTON_TEXT}
                  locale={routeLocale}
                  presentation={resolvedPresentation}
                  program={currentProgram}
                  referenceHandler={handleReferences}
                />
              )
            )}
            {!hasPhaseSections && showAdvisoryActivities && (
              <AdvisoryActivitiesContainer>
                <AdvisoryActivitiesTitle>
                  Advisory Activity Links
                </AdvisoryActivitiesTitle>
                <AdvisoryActivitiesGrid>
                  {Object.keys(advisoryActivityGroups).map(advisoryType => (
                    <AdvisoryActivityLink
                      advisoryType={advisoryActivityGroups[advisoryType]}
                      key={advisoryType}
                      productName={match?.params?.program || 'middle-school'}
                    />
                  ))}
                </AdvisoryActivitiesGrid>
              </AdvisoryActivitiesContainer>
            )}
            {!!references && (
              <HsIndividualResourceReferences
                audience={audience}
                focusedReference={reference}
                isOpen={openAccordionReference}
                ref={supRef}
                references={references}
                setAccordionCollapse={setOpenAccordionReference}
              />
            )}
          </div>
        </DetailsContainer>
      </ResourceContainer>
    </PageContainer>
  )
}

IndividualResource.propTypes = {
  entry: PropTypes.object,
  gradeInstructorData: PropTypes.object,
  isHsPreview: PropTypes.bool,
  isPreview: PropTypes.bool,
  match: PropTypes.func,
  navigationEvent: PropTypes.func,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.object,
  userContext: PropTypes.object,
}

export default withSiteContext(IndividualResource, {
  useGuard: true,
})
